import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, X, ChevronRight, Pill, AlertCircle, Database, Shield } from 'lucide-react';
import { getDrugs, getTherapeuticAreas } from '../services/api';

const CONF_COLORS = {
  high:    { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  moderate:{ bg: 'bg-amber-50 border-amber-200',    text: 'text-amber-700',   dot: 'bg-amber-500' },
  low:     { bg: 'bg-red-50 border-red-200',         text: 'text-red-700',     dot: 'bg-red-500' },
  unknown: { bg: 'bg-slate-100 border-slate-200',    text: 'text-slate-500',   dot: 'bg-slate-400' },
};

function DrugCard({ drug }) {
  const conf = drug.confidence_level || drug.confidence_summary?.level || 'unknown';
  const confStyle = CONF_COLORS[conf] || CONF_COLORS.unknown;
  const sourceCount = drug.source_count || drug.confidence_summary?.source_count || 0;

  return (
    <Link 
      to={`/drug/${drug.id}`}
      className="bg-white border border-slate-200 rounded-2xl p-5 block group hover:shadow-lg hover:border-slate-300 transition-all"
      data-testid={`drug-card-${drug.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <Pill className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 group-hover:text-[#0A2540] transition-colors">
              {drug.brand_name}
            </h3>
            <p className="text-sm text-slate-500">{drug.generic_name}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold rounded-full border px-2 py-0.5 ${confStyle.bg} ${confStyle.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${confStyle.dot}`} />
          {conf.charAt(0).toUpperCase() + conf.slice(1)}
        </span>
      </div>

      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{drug.indication}</p>

      <div className="flex items-center flex-wrap gap-2 mb-3">
        <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">{drug.therapeutic_area}</span>
        <span className="text-xs text-slate-400">{drug.formulation} {drug.strength}</span>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-500">
            <span className="font-semibold text-slate-800">{drug.generic_count || 0}</span> generics
          </span>
          <span className="text-slate-300">|</span>
          <span className="flex items-center gap-1 text-slate-400"><Database className="w-3 h-3" /> {sourceCount} sources</span>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#0A2540] transition-colors" />
      </div>
    </Link>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('query') || '');
  const [therapeuticArea, setTherapeuticArea] = useState(searchParams.get('therapeutic_area') || '');
  const [drugs, setDrugs] = useState([]);
  const [therapeuticAreas, setTherapeuticAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    async function fetchAreas() {
      try {
        const res = await getTherapeuticAreas();
        setTherapeuticAreas(res.data.therapeutic_areas || []);
      } catch (error) {
        console.error('Failed to fetch therapeutic areas:', error);
      }
    }
    fetchAreas();
  }, []);

  useEffect(() => {
    async function fetchDrugs() {
      setLoading(true);
      try {
        const params = {};
        if (query) params.query = query;
        if (therapeuticArea) params.therapeutic_area = therapeuticArea;
        
        const res = await getDrugs(params);
        setDrugs(res.data.drugs || []);
        setTotal(res.data.total || 0);
      } catch (error) {
        console.error('Failed to fetch drugs:', error);
      } finally {
        setLoading(false);
      }
    }
    
    const debounce = setTimeout(fetchDrugs, 300);
    return () => clearTimeout(debounce);
  }, [query, therapeuticArea]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(prev => {
      if (query) prev.set('query', query);
      else prev.delete('query');
      return prev;
    });
  };

  const clearFilters = () => {
    setQuery('');
    setTherapeuticArea('');
    setSearchParams({});
  };

  return (
    <div className="p-6" data-testid="search-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Drug Search</h1>
        <p className="text-slate-500">Search by brand name, generic molecule, or therapeutic area</p>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Eliquis, Humira, atorvastatin..."
              className="input-field pl-10 py-3"
              data-testid="search-input"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary flex items-center gap-2 ${showFilters ? 'bg-slate-100' : ''}`}
            data-testid="filter-toggle-btn"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          <button type="submit" className="btn-primary" data-testid="search-submit-btn">
            Search
          </button>
        </div>
      </form>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card-premium p-4 mb-6 animate-fade-in" data-testid="filters-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Filters</h3>
            <button onClick={clearFilters} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
              <X className="w-4 h-4" />
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Therapeutic Area</label>
              <select
                value={therapeuticArea}
                onChange={(e) => setTherapeuticArea(e.target.value)}
                className="select-field"
                data-testid="therapeutic-area-select"
              >
                <option value="">All Areas</option>
                {therapeuticAreas.map(area => (
                  <option key={area.name} value={area.name}>{area.name} ({area.count})</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters */}
      {(query || therapeuticArea) && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-slate-500">Active filters:</span>
          {query && (
            <span className="badge-info flex items-center gap-1">
              Search: {query}
              <button onClick={() => setQuery('')} className="hover:text-blue-900">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {therapeuticArea && (
            <span className="badge-info flex items-center gap-1">
              {therapeuticArea}
              <button onClick={() => setTherapeuticArea('')} className="hover:text-blue-900">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Results */}
      <div className="mb-4">
        <p className="text-sm text-slate-500">
          {loading ? 'Searching...' : `${total} results found`}
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="card-premium p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="skeleton w-10 h-10 rounded-lg" />
                <div className="flex-1">
                  <div className="skeleton h-5 w-24 mb-1" />
                  <div className="skeleton h-4 w-20" />
                </div>
              </div>
              <div className="skeleton h-12 w-full mb-3" />
              <div className="skeleton h-4 w-32" />
            </div>
          ))}
        </div>
      ) : drugs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {drugs.map(drug => (
            <DrugCard key={drug.id} drug={drug} />
          ))}
        </div>
      ) : (
        <div className="card-premium p-12 text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">No drugs found</p>
          <p className="text-sm text-slate-500 mt-1">Try adjusting your search or filters</p>
          <button onClick={clearFilters} className="btn-primary mt-4">
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
